/*
  <capra-collection>: swaps the product grid in place when a sort chip, a
  filter or a Show more link is followed, fetching only this section
  through the Section Rendering API, and keeps the address bar in step so
  the page can be reloaded or shared. Without JavaScript the same links load
  the page normally and the filter form submits.

  Filters are the store's own: the inputs in a filter form are named after
  Shopify's filter.* parameters, so a change rebuilds the address from them
  and refetches. The results and every region named data-capra-region (the
  filter column, the sheet body) are refreshed from the response, keeping
  focus, scroll and open folds, and holding the control a change came from
  where it sat on screen while the filters around it come and go. Show more appends the next page's cards to
  the grid instead of replacing it; with data-capra-infinite it follows
  itself once it nears the screen, so the grid grows as the page scrolls.

  Below desktop the sort and the filters live in a filter sheet, a native
  dialog that slides up from the foot of the screen. Its sort rows are
  radios; Show applies them and closes. A filter change in the sheet
  applies as it is made.

  Markup contract:
    data-section-id           on the element: the section to refetch
    data-capra-infinite       on the element: Show more loads on its own as it scrolls into reach
    data-capra-default-sort   on the element: the collection's own order, which does not
                              count as a sort in use
    data-capra-in-stock       on the element: sold-out products are hidden, so every fetch adds
                              the in-stock filter and the address bar leaves it out; "address"
                              keeps it in the address, where every link already carries it (search)
    data-capra-stock-refresh  on the element: the page loaded without that filter, so its
                              filter counts still hold sold-out products until they are refetched;
                              "results" refetches the grid too, for a page past the first
    [data-capra-results]      wrapper whose innerHTML is replaced (bar, grid, Show more); the
                              open filter panel and open folds inside it survive the swap
    [data-capra-filter]       a filter in the bar's row, by param name: a [data-capra-filter-toggle]
                              button opens its [data-capra-filter-panel]. One is open at a time;
                              the same button, a click outside [data-capra-filter-row] or Esc
                              closes it
    [data-capra-region=name]  blocks refreshed alongside the results, matched by name
    [data-capra-aside]        the desktop filter column; the script measures how far down the
                              screen its top sits and publishes it as --capra-aside-top, so the
                              column's cap fits the room below it before the page has scrolled
    form[data-capra-filters]  a filter form; its filter.* inputs build the address
    [data-capra-range]        the price slider: data-capra-range-min and -max inputs,
                              a [data-capra-range-readout] in the same facet, data-money-format
    a[data-capra-remove]      links that drop a filter (pills, Clear links), followed in place
    a[data-capra-more]        the Show more link inside [data-capra-more-wrap]
    [data-capra-shown-cards]  on the Show more count: its [data-capra-shown] number is corrected to
                              the cards in the grid, since search can return short pages
    [data-capra-sort]         sort links; the value is the sort_by, empty for the default
    [data-capra-count-number] the style count in the bar, which the sheet's Show button repeats
    [data-capra-sheet]        the filter dialog, outside the swapped results; inside it
                              input[name=sort] (data-url) rows, [data-capra-sheet-close],
                              [data-capra-sheet-apply] with [data-capra-sheet-apply-count] and
                              [data-capra-sheet-apply-word]
    [data-capra-sheet-open]   a button that opens it; [data-capra-sheet-dot] marks a choice in use
    [data-capra-fab]          the floating one on phones, outside the swapped results; it shows
                              once .capra-collection__bar has scrolled off the top
    [data-capra-search-clear] on the search page, the button that empties the query field in its
                              form; it shows only while the field holds text
*/
if (!customElements.get('capra-collection')) {
  customElements.define(
    'capra-collection',
    class CapraCollection extends HTMLElement {
      connectedCallback() {
        // Before a search there are no results, but the field still works
        this.bindSearchClear();

        this.sectionId = this.dataset.sectionId;
        this.results = this.querySelector('[data-capra-results]');
        if (!this.sectionId || !this.results || !window.fetch) return;

        this.cache = new Map();
        this.inStock = this.hasAttribute('data-capra-in-stock');
        this.stockInAddress = this.dataset.capraInStock === 'address';
        this.sheet = this.querySelector('[data-capra-sheet]');
        if (this.sheet && typeof this.sheet.showModal !== 'function') this.sheet = null;
        this.aside = this.querySelector('[data-capra-aside]');

        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.releaseAnchor = this.releaseAnchor.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);
        this.addEventListener('input', this.onInput);
        window.addEventListener('popstate', this.onPopState);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        if (this.sheet) this.sheet.addEventListener('cancel', this.onCancel);
        if (this.aside) {
          this.aside.addEventListener('pointerleave', this.releaseAnchor);
          this.onViewportChange = this.onViewportChange.bind(this);
          window.addEventListener('scroll', this.onViewportChange, { passive: true });
          window.addEventListener('resize', this.onViewportChange);
          // The column slides with the header as it hides and returns, after the scroll that moved it
          this.aside.addEventListener('transitionend', this.onViewportChange);
          this.fitAside();
        }
        if (this.hasAttribute('data-capra-infinite') && 'IntersectionObserver' in window) {
          // Starts the next page a screen or so before the button shows
          this.moreObserver = new IntersectionObserver((entries) => this.onMoreVisible(entries), {
            rootMargin: '0px 0px 100% 0px',
          });
          this.observeMore();
        }
        this.fab = this.querySelector('[data-capra-fab]');
        if (this.fab && 'IntersectionObserver' in window) {
          // The bar's root runs far below the screen, so the bar only stops
          // intersecting once it is above it, however far a jump carries it.
          // The grid's root is the strip of screen the button sits in.
          this.fabObserver = new IntersectionObserver((entries) => this.onFabEntries(entries), {
            rootMargin: '0px 0px 1000000px 0px',
          });
          this.fabGridObserver = new IntersectionObserver((entries) => this.onFabEntries(entries), {
            rootMargin: '-85% 0px 0px 0px',
          });
          this.fabGridObserver.observe(this.results);
          this.observeBar();
        }

        // Mark the entry the page loaded on, so going back to it from a sort
        // or a filter change is handled like any other history step.
        history.replaceState(Object.assign({}, history.state, { capraCollection: true }), '');
        this.syncTrigger();
        this.querySelectorAll('[data-capra-range]').forEach((range) => this.syncRange(range));
        if (this.inStock && this.hasAttribute('data-capra-stock-refresh')) {
          this.refreshStock(this.dataset.capraStockRefresh === 'results');
        }
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        document.removeEventListener('pointerdown', this.releaseAnchor);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        if (this.moreObserver) this.moreObserver.disconnect();
        if (this.fabObserver) this.fabObserver.disconnect();
        if (this.fabGridObserver) this.fabGridObserver.disconnect();
        if (this.aside) {
          window.removeEventListener('scroll', this.onViewportChange);
          window.removeEventListener('resize', this.onViewportChange);
          cancelAnimationFrame(this.asideFrame);
        }
      }

      bindSearchClear() {
        const clear = this.querySelector('[data-capra-search-clear]');
        const input = clear && clear.form && clear.form.querySelector('input[type="search"]');
        if (!input || this.searchClearBound) return;
        this.searchClearBound = true;
        const sync = () => {
          clear.hidden = input.value === '';
        };
        input.addEventListener('input', sync);
        clear.addEventListener('click', () => {
          input.value = '';
          sync();
          input.focus();
        });
      }

      onViewportChange() {
        if (this.asideFrame) return;
        this.asideFrame = requestAnimationFrame(() => {
          this.asideFrame = 0;
          this.fitAside();
        });
      }

      // The column is sticky and scrolls on its own, capped to the room under
      // its stuck position. At the top of the page its top edge sits lower,
      // under the title or the hero, so that cap would run past the fold and
      // the last filters could not be reached until the page had scrolled.
      // Its distance from the top of the screen is the one thing CSS cannot
      // see, so it is measured here and the stylesheet does the rest.
      fitAside() {
        const top = Math.max(0, Math.round(this.aside.getBoundingClientRect().top));
        if (top === this.asideTop) return;
        this.asideTop = top;
        this.aside.style.setProperty('--capra-aside-top', `${top}px`);
      }

      onClick(event) {
        const target = event.target;
        const toggle = target.closest('[data-capra-filter-toggle]');
        if (toggle && this.contains(toggle)) {
          const item = toggle.closest('[data-capra-filter]');
          if (item) this.toggleFilter(item.dataset.capraFilter);
          return;
        }
        const opener = target.closest('[data-capra-sheet-open]');
        if (opener && this.contains(opener)) {
          this.sheetOpener = opener;
          this.openSheet();
          return;
        }
        if (target.closest('[data-capra-sheet-close]')) {
          this.closeSheet();
          return;
        }
        if (target.closest('[data-capra-sheet-apply]')) {
          this.applySheet();
          return;
        }
        // A click on the dialog itself, outside its panel, is on the backdrop
        if (this.sheet && target === this.sheet) {
          this.closeSheet();
          return;
        }

        const link = target.closest('a[data-capra-sort], a[data-capra-remove], a[data-capra-more]');
        if (!link || !this.contains(link)) return;
        // Modified clicks keep their browser meaning (new tab, download, ...)
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();
        if (link.hasAttribute('data-capra-more')) {
          this.loadMore(link, { focus: true });
          return;
        }

        const isSort = link.hasAttribute('data-capra-sort');
        this.anchorSource = link;
        this.load(new URL(link.href, location.href), {
          push: true,
          focus: isSort ? { sort: link.dataset.capraSort } : { keep: true },
          toTop: !isSort,
        });
      }

      /* A filter input applies itself, after a short pause so a run of
         clicks becomes one fetch */
      onChange(event) {
        const form = event.target.closest('form[data-capra-filters]');
        if (!form || !this.contains(form)) return;
        this.anchorSource = event.target;
        clearTimeout(this.filterTimer);
        this.filterTimer = setTimeout(() => this.applyFilters(form), 250);
      }

      /* The price slider's readout and fill follow the thumbs as they move */
      onInput(event) {
        const range = event.target.closest('[data-capra-range]');
        if (range && event.target.matches('input[type="range"]')) this.syncRange(range, event.target);
      }

      onCancel(event) {
        event.preventDefault();
        this.closeSheet();
      }

      onPopState(event) {
        if (!event.state || !event.state.capraCollection) return;
        this.load(new URL(location.href), { push: false });
      }

      /* ---- Filter panels ----
         The bar is rebuilt with the results, so the open panel is known by
         its filter's param name rather than by element. Opening one leaves
         focus on its button; the panel follows it in the tab order. */

      filterItem(key) {
        return [...this.results.querySelectorAll('[data-capra-filter]')].find(
          (item) => item.dataset.capraFilter === key
        );
      }

      setFilterOpen(item, open) {
        const toggle = item.querySelector('[data-capra-filter-toggle]');
        const panel = item.querySelector('[data-capra-filter-panel]');
        if (toggle) toggle.setAttribute('aria-expanded', String(open));
        if (panel) panel.hidden = !open;
      }

      toggleFilter(key) {
        if (this.openFilterKey === key) this.closeFilter();
        else this.openFilter(key);
      }

      openFilter(key) {
        this.closeFilter();
        const item = this.filterItem(key);
        if (!item) return;
        this.setFilterOpen(item, true);
        this.openFilterKey = key;
      }

      closeFilter({ focus = false } = {}) {
        const key = this.openFilterKey;
        if (!key) return;
        this.openFilterKey = null;
        const item = this.filterItem(key);
        if (!item) return;
        this.setFilterOpen(item, false);
        const toggle = item.querySelector('[data-capra-filter-toggle]');
        if (focus && toggle) toggle.focus();
      }

      onDocumentClick(event) {
        if (!this.openFilterKey) return;
        if (event.target.closest && event.target.closest('[data-capra-filter-row]')) return;
        this.closeFilter();
      }

      onKeydown(event) {
        if (event.key !== 'Escape' || !this.openFilterKey) return;
        event.preventDefault();
        this.closeFilter({ focus: true });
      }

      /* ---- Filters ---- */

      applyFilters(form) {
        this.load(this.filterUrl(form), { push: true, focus: { keep: true }, toTop: true });
      }

      /* The address rebuilt from a filter form: every filter.* parameter is
         replaced by the form's, a slider thumb at its end sends nothing,
         and a change starts again from page one. */
      filterUrl(form) {
        const url = new URL(location.href);
        [...url.searchParams.keys()]
          .filter((key) => key.startsWith('filter.'))
          .forEach((key) => url.searchParams.delete(key));
        url.searchParams.delete('page');

        form.querySelectorAll('input[name^="filter."]').forEach((input) => {
          if (input.disabled) return;
          if (input.type === 'checkbox' || input.type === 'radio') {
            if (input.checked) url.searchParams.append(input.name, input.value);
            return;
          }
          if (input.type === 'range') {
            const atEnd = input.hasAttribute('data-capra-range-min')
              ? Number(input.value) <= Number(input.min)
              : Number(input.value) >= Number(input.max);
            if (!atEnd) url.searchParams.append(input.name, input.value);
            return;
          }
          if (input.value !== '') url.searchParams.append(input.name, input.value);
        });
        return url;
      }

      /* Keeps the two thumbs in order, paints the span between them and
         writes the readout. The thumb nearer the top end takes the top
         layer so both stay reachable. */
      syncRange(range, moved) {
        const min = range.querySelector('[data-capra-range-min]');
        const max = range.querySelector('[data-capra-range-max]');
        if (!min || !max) return;
        if (Number(min.value) > Number(max.value)) {
          if (moved === max) max.value = min.value;
          else min.value = max.value;
        }
        const floor = Number(min.min) || 0;
        const ceiling = Number(max.max) || 1;
        const span = Math.max(ceiling - floor, 1);
        const low = ((Number(min.value) - floor) / span) * 100;
        const high = ((Number(max.value) - floor) / span) * 100;
        range.style.setProperty('--capra-range-low', `${low}%`);
        range.style.setProperty('--capra-range-high', `${high}%`);
        range.classList.toggle('capra-range--min-top', low > 50);

        const facet = range.closest('.capra-facet') || range.parentElement;
        const readout = facet && facet.querySelector('[data-capra-range-readout]');
        if (readout) readout.textContent = `${this.money(min.value, range)} – ${this.money(max.value, range)}`;
      }

      money(units, range) {
        const amount = Number(units) || 0;
        if (typeof window.capraFormatMoney === 'function' && range.dataset.moneyFormat) {
          // Whole amounts drop their zero cents, as money_without_trailing_zeros does
          return window.capraFormatMoney(Math.round(amount * 100), range.dataset.moneyFormat).replace(/[.,]00(?=\D|$)/, '');
        }
        return `${range.dataset.currency || ''}${amount}`;
      }

      /* The in-stock filter is the page's, not a choice the shopper made */
      activeFilters() {
        return [...new URL(location.href).searchParams.keys()].some(
          (key) => key.startsWith('filter.') && !(this.inStock && key === 'filter.v.availability')
        );
      }

      /* ---- Fetching ---- */

      async fetchSection(url) {
        const fetchUrl = new URL(url);
        fetchUrl.searchParams.set('section_id', this.sectionId);
        if (this.inStock) fetchUrl.searchParams.set('filter.v.availability', '1');
        let html = this.cache.get(fetchUrl.href);
        if (!html) {
          const response = await fetch(fetchUrl.href);
          if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
          html = await response.text();
          this.cache.set(fetchUrl.href, html);
        }
        return new DOMParser().parseFromString(html, 'text/html');
      }

      async load(url, { push = false, focus = null, toTop = false } = {}) {
        this.results.classList.add('is-loading');
        this.results.setAttribute('aria-busy', 'true');
        const request = (this.request = {});

        let doc;
        try {
          doc = await this.fetchSection(url);
        } catch (error) {
          // A full page load is the safest fallback
          window.location.assign(url.href);
          return;
        }
        // A later click superseded this one while it was in flight
        if (request !== this.request) return;

        const next = doc.querySelector('[data-capra-results]');
        if (!next) {
          window.location.assign(url.href);
          return;
        }

        const kept = focus && focus.keep ? this.rememberFocus() : null;
        const anchor = focus && focus.keep ? this.rememberAnchor() : null;
        this.swapResults(next);
        this.swapRegions(doc);
        this.results.classList.remove('is-loading');
        this.results.setAttribute('aria-busy', 'false');

        if (push) history.pushState({ capraCollection: true }, '', this.address(url).href);
        this.syncTrigger();
        this.querySelectorAll('[data-capra-range]').forEach((range) => this.syncRange(range));
        if (this.sheet && this.sheet.open) this.updateApplyCount();
        if (kept) this.restoreKeptFocus(kept);
        else this.restoreFocus(focus);
        if (anchor) this.holdAnchor(anchor);
        if (toTop) this.scrollToResults();
      }

      /* A filter change starts the list again from its top when the shopper
         had scrolled into it. The header is asked to stay away on the way
         up, and on phones the list stops under the status bar, the inset
         the stuck header pads itself by. */
      scrollToResults() {
        const top = this.results.getBoundingClientRect().top;
        if (top >= 0) return;
        const header = document.querySelector('sticky-header');
        const inset = header ? parseFloat(getComputedStyle(header).paddingTop) || 0 : 0;
        if (header) header.dispatchEvent(new Event('preventHeaderReveal'));
        window.scrollTo({ top: window.scrollY + top - inset });
      }

      /* The in-stock filter rides on every fetch, so the address leaves it out */
      address(url) {
        if (!this.inStock || this.stockInAddress) return url;
        const address = new URL(url);
        address.searchParams.delete('filter.v.availability');
        return address;
      }

      /* A page loaded without the in-stock filter shows only in-stock cards,
         but its filter counts still hold sold-out products, and past the
         first page its cards are not the filtered page's. The filtered
         section brings both; whatever the shopper has already started wins
         over it, and a failed fetch leaves the page as it loaded. */
      async refreshStock(withResults) {
        const request = this.request;
        let doc;
        try {
          doc = await this.fetchSection(new URL(location.href));
        } catch (error) {
          return;
        }
        if (request !== this.request || this.filterTimer || this.moreLoading) return;
        const kept = this.rememberFocus();
        const next = withResults && doc.querySelector('[data-capra-results]');
        if (next) this.swapResults(next);
        this.swapRegions(doc);
        this.querySelectorAll('[data-capra-range]').forEach((range) => this.syncRange(range));
        if (kept) this.restoreKeptFocus(kept);
      }

      /* The results take the new markup, keeping open folds and reopening
         the filter panel that was open, at the same scroll */
      swapResults(next) {
        const open = new Set(
          [...this.results.querySelectorAll('details[data-capra-key][open]')].map((details) => details.dataset.capraKey)
        );
        const openItem = this.openFilterKey && this.filterItem(this.openFilterKey);
        const openPanel = openItem && openItem.querySelector('[data-capra-filter-panel]');
        const panelScroll = openPanel ? openPanel.scrollTop : 0;

        this.results.innerHTML = next.innerHTML;
        this.results.querySelectorAll('details[data-capra-key]').forEach((details) => {
          if (open.has(details.dataset.capraKey)) details.open = true;
        });
        if (this.openFilterKey) {
          const item = this.filterItem(this.openFilterKey);
          if (item) {
            this.setFilterOpen(item, true);
            const panel = item.querySelector('[data-capra-filter-panel]');
            if (panel) panel.scrollTop = panelScroll;
          } else {
            this.openFilterKey = null;
          }
        }
        this.settleCards(this.results);
        this.observeMore();
        this.observeBar();
      }

      /* Dawn's scroll reveal would otherwise hold new cards at opacity 0 */
      settleCards(root) {
        root.querySelectorAll('.scroll-trigger').forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
      }

      /* Regions outside the results (the filter column, the sheet body)
         take their counterpart's content, keeping their scroll position and
         whichever folds were open. */
      swapRegions(doc) {
        this.querySelectorAll('[data-capra-region]').forEach((region) => {
          const name = region.dataset.capraRegion;
          const next = doc.querySelector(`[data-capra-region="${CSS.escape(name)}"]`);
          if (!next) return;
          const open = new Set(
            [...region.querySelectorAll('details[data-capra-key][open]')].map((details) => details.dataset.capraKey)
          );
          const scrollTop = region.scrollTop;
          region.innerHTML = next.innerHTML;
          region.querySelectorAll('details[data-capra-key]').forEach((details) => {
            if (open.has(details.dataset.capraKey)) details.open = true;
          });
          region.scrollTop = scrollTop;
        });
      }

      /* The focused input is replaced with its region; note it by region,
         name and value so the same input in the new markup can take the
         focus back. The region matters: the column and the sheet hold the
         same inputs, and only one of them is on screen. */
      rememberFocus() {
        const active = document.activeElement;
        if (!active || !this.contains(active)) return null;
        const region = active.closest('[data-capra-region]');
        if (active.matches('input[name]')) {
          return { name: active.name, value: active.value, region: region ? region.dataset.capraRegion : null };
        }
        // The All chip turns into a plain label, so focus lands on the group's first chip
        if (active.matches('[data-capra-facet-all]')) {
          const facet = active.closest('[data-capra-facet]');
          if (facet) return { name: facet.dataset.capraFacet, value: '', region: region ? region.dataset.capraRegion : null };
        }
        // A Clear link in a filter panel hands focus to the panel's button
        const item = active.closest('[data-capra-filter]');
        if (item && active.matches('[data-capra-remove]')) return { filter: item.dataset.capraFilter };
        if (active.matches('[data-capra-remove]')) return { grid: true };
        return null;
      }

      restoreKeptFocus(kept) {
        let target = null;
        if (kept.name !== undefined) {
          const scope = (kept.region && this.querySelector(`[data-capra-region="${CSS.escape(kept.region)}"]`)) || this;
          target = scope.querySelector(
            `input[name="${CSS.escape(kept.name)}"][value="${CSS.escape(kept.value)}"]:not(:disabled)`
          );
          if (!target) target = scope.querySelector(`input[name="${CSS.escape(kept.name)}"]:not(:disabled)`);
        } else if (kept.filter !== undefined) {
          const item = this.filterItem(kept.filter);
          target = item && item.querySelector('[data-capra-filter-toggle]');
        } else if (kept.grid) {
          target = this.results.querySelector('.capra-collection__grid > li .capra-card__link, [data-capra-remove]');
        }
        if (target) target.focus({ preventScroll: true });
      }

      /* ---- Anchoring ---- */

      /* A change can add the band of active filters and take values off the
         filters above the one that was used, which would slide it out from
         under the pointer. The control the change came from is noted by
         region, filter, name and value, with where it sat on screen. */
      rememberAnchor() {
        const source = this.anchorSource;
        this.anchorSource = null;
        if (!source || !source.isConnected) return null;
        const region = source.closest('[data-capra-region]');
        const facet = source.closest('[data-capra-facet]');
        if (!region || !facet) return null;
        return {
          region: region.dataset.capraRegion,
          facet: facet.dataset.capraFacet,
          name: source.name,
          value: source.value,
          top: (source.closest('label') || source).getBoundingClientRect().top,
          facetTop: facet.getBoundingClientRect().top,
        };
      }

      /* Puts the same control back where it sat: the column or the sheet
         scrolls by the difference, and what scrolling cannot reach becomes
         space, above the filter when it rose and under the filters when it
         sank. A control that is gone (a Clear link) holds its filter's top
         instead. Space is only held under a pointer, which can let go of it. */
      holdAnchor(anchor) {
        const region = this.querySelector(`[data-capra-region="${CSS.escape(anchor.region)}"]`);
        if (!region) return;
        const scroller = region.closest('[data-capra-aside]') || region;
        const facet = [...region.querySelectorAll('[data-capra-facet]')].find(
          (item) => item.dataset.capraFacet === anchor.facet
        );
        if (!facet) return;
        const input =
          anchor.name &&
          [...facet.querySelectorAll('input[name]')].find(
            (item) => item.name === anchor.name && (item.type === 'range' || item.value === anchor.value)
          );
        const row = input ? input.closest('label') || input : facet;
        const was = input ? anchor.top : anchor.facetTop;

        let space = scroller.querySelector(':scope > [data-capra-anchor-space]');
        if (space) {
          space.classList.remove('is-settling');
          space.style.height = '';
        }
        if (scroller === this.aside) this.fitAside();

        const offset = () => row.getBoundingClientRect().top - was;
        const scrollBy = (amount) => {
          scroller.scrollTop += amount;
          return offset();
        };
        let rest = scrollBy(offset());
        if (Math.abs(rest) < 1 || !scroller.matches(':hover')) return;

        if (rest < 0) {
          facet.style.marginTop = `${-rest}px`;
        } else {
          if (!space) {
            // A span, as Dawn hides every empty div
            space = document.createElement('span');
            space.className = 'capra-anchor-space';
            space.setAttribute('data-capra-anchor-space', '');
            space.setAttribute('aria-hidden', 'true');
            scroller.append(space);
          }
          // A scroller short of its cap grows before it scrolls
          let height = 0;
          for (let pass = 0; pass < 12 && rest >= 1; pass += 1) {
            height += rest;
            space.style.height = `${height}px`;
            rest = scrollBy(rest);
          }
        }
        this.anchorScroller = scroller;
        document.addEventListener('pointerdown', this.releaseAnchor);
      }

      /* The held space closes once the pointer leaves the filters, or is
         pressed anywhere outside them */
      releaseAnchor(event) {
        const scroller = this.anchorScroller;
        if (!scroller) return;
        if (event && event.type === 'pointerdown' && scroller.contains(event.target)) return;
        this.anchorScroller = null;
        document.removeEventListener('pointerdown', this.releaseAnchor);
        scroller.querySelectorAll('[data-capra-facet]').forEach((facet) => {
          if (!facet.style.marginTop) return;
          facet.classList.add('is-settling');
          facet.style.marginTop = '0px';
        });
        const space = scroller.querySelector(':scope > [data-capra-anchor-space]');
        if (space) {
          space.classList.add('is-settling');
          space.style.height = '';
        }
      }

      /* ---- Show more ---- */

      observeMore() {
        if (!this.moreObserver) return;
        this.moreObserver.disconnect();
        const wrap = this.results.querySelector('[data-capra-more-wrap]');
        if (wrap) this.moreObserver.observe(wrap);
      }

      onMoreVisible(entries) {
        if (this.moreLoading || !entries.some((entry) => entry.isIntersecting)) return;
        const link = this.results.querySelector('[data-capra-more-wrap] a[data-capra-more]');
        if (link) this.loadMore(link, { auto: true });
      }

      /* The next page's cards join the grid and its own Show more takes this
         one's place. A load the scroll started leaves focus alone, and on a
         failure stops and leaves the button to be pressed, rather than
         moving the page out from under the shopper. */
      async loadMore(link, { auto = false, focus = false } = {}) {
        if (this.moreLoading) return;
        this.moreLoading = true;
        const wrap = link.closest('[data-capra-more-wrap]') || link;
        wrap.classList.add('is-loading');
        link.setAttribute('aria-busy', 'true');
        const url = new URL(link.href, location.href);
        const request = this.request;
        const fail = () => {
          this.moreLoading = false;
          if (!auto) {
            window.location.assign(link.href);
            return;
          }
          if (this.moreObserver) this.moreObserver.disconnect();
          wrap.classList.remove('is-loading');
          link.removeAttribute('aria-busy');
        };

        let doc;
        try {
          doc = await this.fetchSection(url);
        } catch (error) {
          fail();
          return;
        }
        // A sort or a filter replaced the grid while this page was loading
        if (request !== this.request || !this.results.contains(wrap)) {
          this.moreLoading = false;
          return;
        }
        const grid = this.results.querySelector('.capra-collection__grid');
        const nextGrid = doc.querySelector('.capra-collection__grid');
        if (!grid || !nextGrid) {
          fail();
          return;
        }

        const items = [...nextGrid.children];
        items.forEach((item) => grid.appendChild(item));
        this.settleCards(grid);
        const nextWrap = doc.querySelector('[data-capra-more-wrap]');
        const shown = doc.querySelector('.capra-collection__more');
        if (nextWrap) wrap.replaceWith(nextWrap);
        else if (shown) wrap.replaceWith(shown);
        else wrap.remove();
        const tally = this.results.querySelector('[data-capra-shown-cards] [data-capra-shown]');
        if (tally) tally.textContent = grid.children.length;
        this.moreLoading = false;
        this.observeMore();

        const focusTarget = focus && items[0] && items[0].querySelector('.capra-card__link');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }

      /* The sort the server rendered: the active chip carries it */
      currentSort() {
        const active = this.results.querySelector('a[data-capra-sort].capra-chip--active');
        return active ? active.dataset.capraSort : '';
      }

      /* A sort counts as a choice only when it is not the collection's own order */
      sortInUse() {
        const sort = this.currentSort();
        return Boolean(sort) && sort !== (this.dataset.capraDefaultSort || '');
      }

      /* The sheet button's dot shows while a sort or a filter is in use */
      syncTrigger() {
        const inUse = this.sortInUse() || this.activeFilters();
        this.querySelectorAll('[data-capra-sheet-dot]').forEach((dot) => {
          dot.hidden = !inUse;
        });
      }

      /* ---- The floating sheet button ----
         On phones the bar scrolls away with the page, and the button stands
         in for it from then until the results end. The bar is rebuilt with the
         results, so it is watched again after each swap. */

      observeBar() {
        if (!this.fabObserver) return;
        if (this.fabBar) this.fabObserver.unobserve(this.fabBar);
        this.fabBar = this.results.querySelector('.capra-collection__bar');
        if (this.fabBar) this.fabObserver.observe(this.fabBar);
        else this.fabPastBar = false;
        this.syncFab();
      }

      onFabEntries(entries) {
        entries.forEach((entry) => {
          if (entry.target === this.results) this.fabInGrid = entry.isIntersecting;
          else if (entry.target === this.fabBar) {
            this.fabPastBar = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          }
        });
        this.syncFab();
      }

      syncFab() {
        this.fab.classList.toggle('is-shown', Boolean(this.fabInGrid && this.fabPastBar));
      }

      /* ---- The filter sheet ---- */

      openSheet() {
        if (!this.sheet || this.sheet.open) return;
        this.syncSheet();
        this.sheet.showModal();
        document.body.classList.add('overflow-hidden');
        // Two frames: the dialog has to be laid out closed before it can slide
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.sheet.classList.add('is-open'));
        });
      }

      closeSheet() {
        if (!this.sheet || !this.sheet.open) return;
        this.sheet.classList.remove('is-open');
        const finish = () => {
          if (this.sheet.open) this.sheet.close();
          document.body.classList.remove('overflow-hidden');
          const opener = this.sheetOpener || this.querySelector('[data-capra-sheet-open]');
          if (opener) opener.focus({ preventScroll: true });
        };
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) finish();
        else setTimeout(finish, 330);
      }

      /* The sort rows reflect the page's state each time the sheet opens,
         so a choice abandoned with Close does not linger. */
      syncSheet() {
        const sort = this.currentSort();
        this.sheet.querySelectorAll('input[name="sort"]').forEach((radio) => {
          radio.checked = radio.value === sort;
        });
        this.updateApplyCount();
      }

      /* The Show button repeats the count the results carry */
      updateApplyCount() {
        const number = this.sheet.querySelector('[data-capra-sheet-apply-count]');
        const word = this.sheet.querySelector('[data-capra-sheet-apply-word]');
        if (!number) return;
        const tally = this.results.querySelector('[data-capra-count-number]');
        const count = tally ? Number(tally.textContent) : NaN;
        if (Number.isNaN(count)) return;
        number.textContent = count;
        if (word) word.textContent = count === 1 ? word.dataset.one : word.dataset.many;
      }

      applySheet() {
        const sortRadio = this.sheet.querySelector('input[name="sort"]:checked');
        if (sortRadio && sortRadio.value !== this.currentSort() && sortRadio.dataset.url) {
          this.load(new URL(sortRadio.dataset.url, location.href), { push: true });
        }
        this.closeSheet();
      }

      /* The clicked sort chip was replaced, so hand focus to its counterpart */
      restoreFocus(focus) {
        if (!focus) return;
        if (focus.sort === undefined) return;
        const target = this.results.querySelector(`[data-capra-sort="${CSS.escape(focus.sort)}"]`);
        if (target) target.focus({ preventScroll: true });
      }
    }
  );
}
